import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/auth/hooks/useAuth';

interface ProtectedCandidateRouteProps {
    children: React.ReactNode;
}

/**
 * Protected Route wrapper for Candidate pages
 * Ensures only authenticated candidate users can access candidate routes
 * Company/Recruiter users are redirected to their dashboard
 */
const ProtectedCandidateRoute: React.FC<ProtectedCandidateRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F1F3F4] flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6] mb-4"></div>
                    <p className="text-base font-medium text-[#757575]">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
    }

    // Check if user is candidate or admin (admins can access candidate routes for support)
    const userRole = user?.userType;
    const hasCandidateAccess = userRole === 'candidate' || userRole === 'hush_admin';

    // Redirect company/recruiter users to their dashboard
    if (!hasCandidateAccess) {
        return <Navigate to="/companies/dashboard" replace />;
    }

    // User is authenticated and has candidate access
    return <>{children}</>;
};

export default ProtectedCandidateRoute;
